import { useState, useEffect, useMemo } from "react";
import {
    apiService,
    type Prompt,
    type PromptVersion,
    type PromptCreateRequest,
    type Placeholder,
} from "../services/api.service";
import { PROMPT_APPLICATIONS, DEFAULT_APPLICATION_ID } from "../config/api.config";
import { OrgCombobox } from "./users-config/OrgCombobox";
import "./PromptEditor.css";

interface PromptEditorProps {
    prompt: Prompt | null;
    onBack: () => void;
    initialTags?: string;
}

const CATEGORY_OPTIONS = ["system", "user", "assistant", "tool"];

export const PromptEditor = ({ prompt, onBack, initialTags }: PromptEditorProps) => {
    const isNew = !prompt;
    const [activeTab, setActiveTab] = useState<"edit" | "versions">("edit");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [versions, setVersions] = useState<PromptVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<PromptVersion | null>(null);
    const [showNewVersionModal, setShowNewVersionModal] = useState(false);
    const [allPrompts, setAllPrompts] = useState<Prompt[]>([]);
    const [allOrgs, setAllOrgs] = useState<{ id: string; name: string }[]>([]);
    const orgIdToName = useMemo(() => {
        const map: Record<string, string> = {};
        for (const org of allOrgs) map[org.id] = org.name;
        return map;
    }, [allOrgs]);
    const [activeVersion, setActiveVersion] = useState<string>(prompt?.active_version || "");

    // Form state
    const [formData, setFormData] = useState({
        prompt_id: prompt?.prompt_id || "",
        application_id: prompt?.application_id || DEFAULT_APPLICATION_ID,
        name: prompt?.name || "",
        description: prompt?.description || "",
        category: prompt?.category || "system",
        org: prompt?.org || "",
        content: "",
        tags: prompt?.tags?.join(", ") || initialTags || "",
        placeholders: prompt?.placeholders || [],
    });

    const [newVersionData, setNewVersionData] = useState({
        content: "",
        changelog: "",
    });
    const [showPreview, setShowPreview] = useState(false);
    const [previewContent, setPreviewContent] = useState("");
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isEditingPlaceholders, setIsEditingPlaceholders] = useState(false);
    const [isSavingPlaceholders, setIsSavingPlaceholders] = useState(false);
    // Placeholders already persisted on the prompt. Edits must be addition-only:
    // older prompt versions (and tutor versions pinned to them) may still
    // reference these names, so renaming or deleting them is blocked.
    const [savedPlaceholders, setSavedPlaceholders] = useState<Placeholder[]>(prompt?.placeholders || []);
    const savedPlaceholderNames = useMemo(
        () => new Set(savedPlaceholders.map((p) => p.name)),
        [savedPlaceholders]
    );

    useEffect(() => {
        loadAllPrompts();
        if (prompt) {
            loadVersions();
        }
    }, [prompt]);

    const loadAllPrompts = async () => {
        try {
            const data = await apiService.getPrompts();
            setAllPrompts(data);
        } catch {
            // Non-critical — suggestions just won't appear
        }
        try {
            const orgs = await apiService.getOrganizations();
            setAllOrgs(orgs);
        } catch {
            // Non-critical — org name display falls back to raw id
        }
    };

    const loadVersions = async () => {
        if (!prompt) return;
        try {
            const data = await apiService.getPromptVersions(
                prompt.application_id,
                prompt.prompt_id
            );
            setVersions(data);
            // Load active version content
            const activeVersion = data.find((v) => v.version === prompt.active_version);
            if (activeVersion) {
                setSelectedVersion(activeVersion);
                setFormData((prev) => ({ ...prev, content: activeVersion.content }));
            }
        } catch {
            setError("Failed to load versions");
        }
    };

    // Compute prompt hierarchy from active version content
    const promptHierarchy = useMemo(() => {
        if (!prompt || !allPrompts.length) return { uses: [], usedBy: [] };

        const promptIds = new Set(allPrompts.map((p) => p.prompt_id));
        const activeContent = selectedVersion?.content || formData.content || "";

        // Find prompts this prompt references via {{prompt_id}} in its content
        const placeholderRegex = /\{\{(\w[\w-]*)\}\}/g;
        const uses: Prompt[] = [];
        let match;
        while ((match = placeholderRegex.exec(activeContent)) !== null) {
            const refId = match[1];
            if (promptIds.has(refId) && refId !== prompt.prompt_id) {
                const refPrompt = allPrompts.find((p) => p.prompt_id === refId);
                if (refPrompt && !uses.find((u) => u.prompt_id === refId)) {
                    uses.push(refPrompt);
                }
            }
        }

        // Find prompts that reference this prompt — need version content for all prompts
        // For now we check if any prompt has this prompt_id in its placeholders
        const usedBy = allPrompts.filter(
            (p) =>
                p.prompt_id !== prompt.prompt_id &&
                p.placeholders?.some((ph) => ph.name === prompt.prompt_id)
        );

        return { uses, usedBy };
    }, [prompt, allPrompts, selectedVersion, formData.content]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isNew) return;
        setIsSaving(true);
        setError(null);

        try {
            const request: PromptCreateRequest = {
                prompt_id: formData.prompt_id,
                application_id: formData.application_id,
                name: formData.name,
                description: formData.description,
                category: formData.category,
                org: formData.org.trim() || undefined,
                content: formData.content,
                placeholders: formData.placeholders,
                tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            await apiService.createPrompt(request);
            onBack();
        } catch {
            setError("Failed to create prompt");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateVersion = async () => {
        if (!prompt) return;
        setIsSaving(true);
        setError(null);

        try {
            await apiService.createPromptVersion(
                prompt.application_id,
                prompt.prompt_id,
                {
                    content: newVersionData.content,
                    changelog: newVersionData.changelog,
                }
            );
            setShowNewVersionModal(false);
            setNewVersionData({ content: "", changelog: "" });
            loadVersions();
        } catch {
            setError("Failed to create version");
        } finally {
            setIsSaving(false);
        }
    };

    const handleActivateVersion = async (version: string) => {
        if (!prompt) return;
        setIsSaving(true);
        setError(null);

        try {
            await apiService.activatePromptVersion(
                prompt.application_id,
                prompt.prompt_id,
                version
            );
            setActiveVersion(version);
            loadVersions();
        } catch {
            setError("Failed to activate version");
        } finally {
            setIsSaving(false);
        }
    };

    const addPlaceholder = () => {
        // Auto-pick the first unassigned content variable
        const assigned = new Set(formData.placeholders.map((p) => p.name));
        const nextVar = contentVariables.find((v) => !assigned.has(v)) || "";
        setFormData((prev) => ({
            ...prev,
            placeholders: [
                ...prev.placeholders,
                { name: nextVar, description: "", required: true },
            ],
        }));
    };

    const updatePlaceholder = (index: number, field: keyof Placeholder, value: string | boolean) => {
        setFormData((prev) => ({
            ...prev,
            placeholders: prev.placeholders.map((p, i) =>
                i === index ? { ...p, [field]: value } : p
            ),
        }));
    };

    const removePlaceholder = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            placeholders: prev.placeholders.filter((_, i) => i !== index),
        }));
    };

    const handlePreview = async () => {
        setIsLoadingPreview(true);
        try {
            const baseContent = selectedVersion?.content || formData.content || "";
            let resolved = baseContent;

            // For each placeholder mapping, fetch the mapped prompt's active version content
            for (const ph of formData.placeholders) {
                if (!ph.name || !ph.description) continue; // description holds the mapped prompt_id
                if (ph.description === "__runtime__") continue; // runtime params are filled at runtime
                const mappedPrompt = allPrompts.find((p) => p.prompt_id === ph.description);
                if (!mappedPrompt) continue;
                try {
                    const versions = await apiService.getPromptVersions(
                        mappedPrompt.application_id,
                        mappedPrompt.prompt_id
                    );
                    const activeVer = versions.find((v) => v.version === mappedPrompt.active_version) || versions[0];
                    if (activeVer?.content) {
                        // Replace {variable} with the mapped prompt content
                        const pattern = new RegExp(`\\{${ph.name}(?::[^}]*)?\\}`, "g");
                        resolved = resolved.replace(pattern, activeVer.content);
                    }
                } catch {
                    // Leave placeholder as-is if fetch fails
                }
            }
            setPreviewContent(resolved);
            setShowPreview(true);
        } catch {
            setError("Failed to generate preview");
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleSavePlaceholders = async () => {
        if (!prompt) return;
        // Addition-only guard: every already-saved placeholder name must survive.
        const currentNames = new Set(formData.placeholders.map((p) => p.name));
        const removed = savedPlaceholders.filter((p) => !currentNames.has(p.name));
        if (removed.length > 0) {
            setError(
                `Placeholder edits are addition-only — cannot remove or rename: ${removed
                    .map((p) => `{${p.name}}`)
                    .join(", ")}. Older prompt versions may still reference them.`
            );
            return;
        }
        setIsSavingPlaceholders(true);
        setError(null);
        try {
            await apiService.updatePrompt(
                prompt.application_id,
                prompt.prompt_id,
                { placeholders: formData.placeholders }
            );
            setSavedPlaceholders(formData.placeholders);
            setIsEditingPlaceholders(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save placeholders");
        } finally {
            setIsSavingPlaceholders(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Compute the set of prompt_ids that would create a cycle if chosen in the prompt_id dropdown
    // Returns: current prompt id + all ancestors (prompts whose placeholders reference current prompt, directly or transitively)
    const cyclicPromptIds = useMemo((): Set<string> => {
        if (!prompt) return new Set<string>();
        const currentId = prompt.prompt_id;
        const result = new Set<string>();
        result.add(currentId);

        // Build reverse map: prompt_id -> [prompts that reference it via placeholder.prompt_id]
        const reverseMap = new Map<string, string[]>();
        for (const p of allPrompts) {
            for (const ph of p.placeholders ?? []) {
                if (ph.prompt_id) {
                    const refs = reverseMap.get(ph.prompt_id) ?? [];
                    refs.push(p.prompt_id);
                    reverseMap.set(ph.prompt_id, refs);
                }
            }
        }

        // BFS upward from currentId
        const queue = [currentId];
        while (queue.length > 0) {
            const id = queue.shift()!;
            const parents = reverseMap.get(id) ?? [];
            for (const parentId of parents) {
                if (!result.has(parentId)) {
                    result.add(parentId);
                    queue.push(parentId);
                }
            }
        }

        return result;
    }, [prompt, allPrompts]);

    // Parse {variable} patterns from the active content for placeholder dropdown
    // Uses single braces — in Python fstrings, {{}} are escaped brackets, not variables
    const contentVariables = useMemo(() => {
        const content = selectedVersion?.content || formData.content || "";
        const regex = /(?<!\{)\{(\w[\w-]*)(?::[^}]*)?\}(?!\})/g;
        const vars = new Set<string>();
        let m;
        while ((m = regex.exec(content)) !== null) {
            vars.add(m[1]);
        }
        return Array.from(vars);
    }, [selectedVersion, formData.content]);

    return (
        <div className="prompt-editor-container">
            <div className="editor-header">
                <button className="back-btn" onClick={onBack}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    Back to Prompts
                </button>
                <div className="editor-title-row">
                    <h2>{isNew ? "Create New Prompt" : prompt?.name}</h2>
                    {!isNew && formData.org.trim() && <span className="org-badge">{orgIdToName[formData.org.trim()] || formData.org.trim()}</span>}
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                </div>
            )}

            {!isNew && (
                <div className="editor-tabs">
                    <button
                        className={`tab ${activeTab === "edit" ? "active" : ""}`}
                        onClick={() => setActiveTab("edit")}
                    >
                        Overview
                    </button>
                    <button
                        className={`tab ${activeTab === "versions" ? "active" : ""}`}
                        onClick={() => setActiveTab("versions")}
                    >
                        Versions ({versions.length})
                    </button>
                </div>
            )}

            {activeTab === "edit" ? (
                <form onSubmit={handleSubmit} className="editor-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Prompt ID</label>
                            <input
                                type="text"
                                value={formData.prompt_id}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, prompt_id: e.target.value }))
                                }
                                placeholder="e.g., k12_system_prompt"
                                disabled={!isNew}
                                required
                            />
                            {isNew && (
                                <span className="field-hint">
                                    Unique identifier, use snake_case
                                </span>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Application</label>
                            <select
                                value={formData.application_id}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        application_id: e.target.value,
                                    }))
                                }
                                disabled={!isNew}
                                required
                            >
                                {PROMPT_APPLICATIONS.map((app) => (
                                    <option key={app.value} value={app.value}>
                                        {app.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            placeholder="Human-readable name"
                            disabled={!isNew}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, description: e.target.value }))
                            }
                            placeholder="What does this prompt do?"
                            rows={2}
                            disabled={!isNew}
                        />
                    </div>

                    <div className={isNew ? "form-row form-row-triple" : "form-section"}>
                        {!isNew && (
                            <div className="section-header">
                                <h3>Metadata</h3>
                            </div>
                        )}
                        <div className={isNew ? undefined : "form-row form-row-triple"}>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, category: e.target.value }))
                                    }
                                    disabled={!isNew}
                                >
                                    {CATEGORY_OPTIONS.map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Tags</label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, tags: e.target.value }))
                                    }
                                    placeholder="Comma-separated tags"
                                    disabled={!isNew}
                                />
                            </div>
                            <div className="form-group">
                                <label>Organization</label>
                                {isNew ? (
                                    <OrgCombobox
                                        value={formData.org || undefined}
                                        onChange={(id) => setFormData((prev) => ({ ...prev, org: id ?? "" }))}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={orgIdToName[formData.org] || formData.org}
                                        disabled
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {isNew && (
                        <div className="form-group">
                            <label>Initial Content</label>
                            <textarea
                                value={formData.content}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, content: e.target.value }))
                                }
                                placeholder="Enter the prompt content..."
                                rows={12}
                                className="code-textarea"
                                required
                            />
                            <span className="field-hint">
                                Use {"{placeholder}"} syntax for variables
                            </span>
                        </div>
                    )}

                    {/* Prompt Hierarchy — only for existing prompts */}
                    {!isNew && (promptHierarchy.uses.length > 0 || promptHierarchy.usedBy.length > 0) && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Prompt Hierarchy</h3>
                            </div>
                            {promptHierarchy.uses.length > 0 && (
                                <div className="hierarchy-group">
                                    <span className="hierarchy-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hierarchy-icon">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        Uses ({promptHierarchy.uses.length})
                                    </span>
                                    <div className="hierarchy-chips">
                                        {promptHierarchy.uses.map((p) => (
                                            <span key={p.prompt_id} className="hierarchy-chip chip-uses">
                                                {p.name || p.prompt_id}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {promptHierarchy.usedBy.length > 0 && (
                                <div className="hierarchy-group">
                                    <span className="hierarchy-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hierarchy-icon">
                                            <polyline points="18 15 12 9 6 15" />
                                        </svg>
                                        Used by ({promptHierarchy.usedBy.length})
                                    </span>
                                    <div className="hierarchy-chips">
                                        {promptHierarchy.usedBy.map((p) => (
                                            <span key={p.prompt_id} className="hierarchy-chip chip-used-by">
                                                {p.name || p.prompt_id}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Active Version Content — read-only */}
                    {!isNew && selectedVersion && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Active Content (v{selectedVersion.version})</h3>
                            </div>
                            <div className="version-content" style={{ maxHeight: "300px", overflow: "auto" }}>
                                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{selectedVersion.content}</pre>
                            </div>
                        </div>
                    )}

                    {/* Model Configuration — informational */}
                    {!isNew && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Model Configuration</h3>
                            </div>
                            <div className="model-config-info">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="info-icon">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="16" x2="12" y2="12" />
                                    <line x1="12" y1="8" x2="12.01" y2="8" />
                                </svg>
                                <div>
                                    <p className="model-config-text">
                                        LLM, STT, and TTS model configurations are managed via <strong>Tutor Versions</strong>.
                                        A tutor version bundles all active prompts with the model config into an immutable snapshot.
                                    </p>
                                    <p className="model-config-hint">
                                        Manage these settings from the Tutor Versions tab.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="form-section">
                        <div className="section-header">
                            <h3>Placeholders</h3>
                            {isNew && contentVariables.length > 0 && (
                                <button type="button" className="add-btn" onClick={addPlaceholder}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add
                                </button>
                            )}
                            {!isNew && !isEditingPlaceholders && (
                                <button
                                    type="button"
                                    className="add-btn"
                                    onClick={() => setIsEditingPlaceholders(true)}
                                >
                                    Edit
                                </button>
                            )}
                            {!isNew && isEditingPlaceholders && (
                                <div className="flex gap-2">
                                    <button type="button" className="add-btn" onClick={addPlaceholder}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Add
                                    </button>
                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => {
                                            setFormData((prev) => ({ ...prev, placeholders: savedPlaceholders }));
                                            setIsEditingPlaceholders(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="save-btn"
                                        onClick={handleSavePlaceholders}
                                        disabled={isSavingPlaceholders}
                                    >
                                        {isSavingPlaceholders ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            )}
                        </div>
                        {contentVariables.length > 0 ? (
                            <p className="field-hint" style={{ marginBottom: 8 }}>
                                Detected variables from content: {contentVariables.map((v) => `{${v}}`).join(", ")}
                            </p>
                        ) : (
                            <p className="empty-hint">
                                No {"{variables}"} detected in the prompt content.
                            </p>
                        )}
                        {formData.placeholders.length === 0 && !isEditingPlaceholders ? (
                            <p className="empty-hint">
                                {isNew
                                    ? "No placeholders mapped yet. Click Add to map variables to prompts."
                                    : "No placeholders configured for this prompt."}
                            </p>
                        ) : (
                            <div className="placeholders-list">
                                <div className="placeholder-header">
                                    <span>Variable</span>
                                    <span>Source</span>
                                    <span>Prompt ID</span>
                                    <span></span>
                                </div>
                                {formData.placeholders.map((placeholder, index) => (
                                    <div key={index} className="placeholder-item">
                                        {(isNew || isEditingPlaceholders) ? (
                                            <>
                                                {!isNew && savedPlaceholderNames.has(placeholder.name) ? (
                                                    <input
                                                        type="text"
                                                        value={placeholder.name}
                                                        readOnly
                                                        disabled
                                                        title="Existing placeholders can't be renamed — older prompt versions may reference them"
                                                    />
                                                ) : isNew && contentVariables.length > 0 ? (
                                                    <select
                                                        value={placeholder.name}
                                                        onChange={(e) =>
                                                            updatePlaceholder(index, "name", e.target.value)
                                                        }
                                                    >
                                                        <option value="">Select variable...</option>
                                                        {contentVariables.map((v) => (
                                                            <option key={v} value={v}>
                                                                {`{${v}}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={placeholder.name}
                                                        onChange={(e) =>
                                                            updatePlaceholder(index, "name", e.target.value)
                                                        }
                                                        placeholder="variable_name"
                                                    />
                                                )}
                                                {isNew ? (
                                                    <select
                                                        value={placeholder.description}
                                                        onChange={(e) =>
                                                            updatePlaceholder(index, "description", e.target.value)
                                                        }
                                                    >
                                                        <option value="">Select source...</option>
                                                        <option value="__runtime__">Runtime parameter (from DB/code)</option>
                                                        <optgroup label="Prompts">
                                                            {allPrompts
                                                                .filter((p) => p.prompt_id !== prompt?.prompt_id)
                                                                .map((p) => (
                                                                    <option key={p.prompt_id} value={p.prompt_id}>
                                                                        {p.name || p.prompt_id}
                                                                    </option>
                                                                ))
                                                            }
                                                        </optgroup>
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={placeholder.description}
                                                        onChange={(e) =>
                                                            updatePlaceholder(index, "description", e.target.value)
                                                        }
                                                        placeholder="description"
                                                    />
                                                )}
                                                <select
                                                    value={placeholder.prompt_id ?? ""}
                                                    onChange={(e) =>
                                                        updatePlaceholder(index, "prompt_id", e.target.value || "")
                                                    }
                                                    title="Prompt ID (slot assignment)"
                                                >
                                                    <option value="">None (runtime)</option>
                                                    {allPrompts.map((p) => {
                                                        const isCyclic = cyclicPromptIds.has(p.prompt_id);
                                                        return (
                                                            <option
                                                                key={p.prompt_id}
                                                                value={p.prompt_id}
                                                                disabled={isCyclic}
                                                                title={isCyclic ? "Would create a cycle" : undefined}
                                                            >
                                                                {p.name || p.prompt_id}{isCyclic ? " (would create cycle)" : ""}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    onClick={() => removePlaceholder(index)}
                                                    disabled={!isNew && savedPlaceholderNames.has(placeholder.name)}
                                                    title={
                                                        !isNew && savedPlaceholderNames.has(placeholder.name)
                                                            ? "Existing placeholders can't be removed — older prompt versions may reference them"
                                                            : undefined
                                                    }
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <div>{placeholder.name ? `{${placeholder.name}}` : "Not set"}</div>
                                                <div>
                                                    {placeholder.description === "__runtime__"
                                                        ? "Runtime parameter (from DB/code)"
                                                        : allPrompts.find((p) => p.prompt_id === placeholder.description)?.name
                                                            || placeholder.description
                                                            || "Not set"}
                                                </div>
                                                <div>
                                                    {placeholder.prompt_id
                                                        ? (allPrompts.find((p) => p.prompt_id === placeholder.prompt_id)?.name || placeholder.prompt_id)
                                                        : ""}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        {isNew ? (
                            <>
                                <button type="button" className="cancel-btn" onClick={onBack}>
                                    Cancel
                                </button>
                                <button type="submit" className="save-btn" disabled={isSaving}>
                                    {isSaving ? "Saving..." : "Create Prompt"}
                                </button>
                            </>
                        ) : (
                            <>
                                {formData.placeholders.length > 0 && (
                                    <button
                                        type="button"
                                        className="activate-btn"
                                        onClick={handlePreview}
                                        disabled={isLoadingPreview}
                                    >
                                        {isLoadingPreview ? "Loading..." : "Preview"}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </form>
            ) : (
                <div className="versions-panel">
                    <div className="versions-header">
                        <span className="version-count">
                            {versions.length} version{versions.length !== 1 ? "s" : ""}
                        </span>
                        <button
                            className="new-version-btn"
                            onClick={() => {
                                setNewVersionData({
                                    content: selectedVersion?.content || "",
                                    changelog: "",
                                });
                                setShowNewVersionModal(true);
                            }}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            New Version
                        </button>
                    </div>

                    <div className="versions-content">
                        <div className="versions-list">
                            {versions.map((version) => (
                                <div
                                    key={version.version}
                                    className={`version-item ${
                                        selectedVersion?.version === version.version ? "selected" : ""
                                    } ${version.version === activeVersion ? "active" : ""}`}
                                    onClick={() => setSelectedVersion(version)}
                                >
                                    <div className="version-main">
                                        <span className="version-number">v{version.version}</span>
                                        {version.version === activeVersion ? (
                                            <span className="active-badge">Active</span>
                                        ) : (
                                            <span className={`status-badge status-${version.status}`}>
                                                {version.status}
                                            </span>
                                        )}
                                    </div>
                                    <div className="version-meta">
                                        <span>{formatDate(version.created_at)}</span>
                                        {version.created_by && <span>by {version.created_by}</span>}
                                    </div>
                                    {version.changelog && (
                                        <p className="version-changelog">{version.changelog}</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {selectedVersion && (
                            <div className="version-detail">
                                <div className="version-detail-header">
                                    <h3>Version {selectedVersion.version}</h3>
                                    {selectedVersion.version !== activeVersion && (
                                        <div className="version-actions">
                                            <button
                                                className="activate-btn"
                                                onClick={() => handleActivateVersion(selectedVersion.version)}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "..." : "Activate This Version"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="version-content">
                                    <pre>{selectedVersion.content}</pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: 800 }}>
                        <div className="modal-header">
                            <h3>Prompt Preview (Resolved Placeholders)</h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowPreview(false)}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="version-content" style={{ maxHeight: "60vh", overflow: "auto" }}>
                                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{previewContent}</pre>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowPreview(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showNewVersionModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>Create New Version</h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowNewVersionModal(false)}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Content</label>
                                <textarea
                                    value={newVersionData.content}
                                    onChange={(e) =>
                                        setNewVersionData((prev) => ({
                                            ...prev,
                                            content: e.target.value,
                                        }))
                                    }
                                    rows={15}
                                    className="code-textarea"
                                />
                            </div>
                            <div className="form-group">
                                <label>Changelog</label>
                                <input
                                    type="text"
                                    value={newVersionData.changelog}
                                    onChange={(e) =>
                                        setNewVersionData((prev) => ({
                                            ...prev,
                                            changelog: e.target.value,
                                        }))
                                    }
                                    placeholder="What changed in this version?"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowNewVersionModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="save-btn"
                                onClick={handleCreateVersion}
                                disabled={isSaving || !newVersionData.content}
                            >
                                {isSaving ? "Creating..." : "Create Version"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
